import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { playSuccessSound, playErrorSound } from '../utils/sound';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import { postToDiscord } from '../utils/discord';

const APIRecords: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const [selectedReport, setSelectedReport] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [processedData, setProcessedData] = useState<any[]>([]);
  const [authLoading, setAuthLoading] = useState(true);

  // Wait for auth to load
  useEffect(() => {
    const timer = setTimeout(() => {
      setAuthLoading(false);
    }, 1000); // Give 1 second for auth to initialize
    
    return () => clearTimeout(timer);
  }, []);

  // If still loading auth, show loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  const reportTypes = [
    'PaySprint Credit Ledger',
    'PaySprint Debit Ledger',
    'ICICI Bank Statement',
    'EasyCash API',
    'M-Robotics API',
    'AePS Report',
    'Cashout Report',
    'Recharge Report'
  ];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedReport) {
      toast.error('Please select a report type');
      playErrorSound();
      return;
    }
    
    if (!selectedFile) {
      toast.error('Please attach a report file');
      playErrorSound();
      return;
    }

    setLoading(true);
    
    try {
      const fileData = await processFile(selectedFile, selectedReport);
      
      setProcessedData(prev => [...prev, {
        reportType: selectedReport,
        fileName: selectedFile.name,
        data: fileData,
        timestamp: new Date().toISOString()
      }]);
      
      toast.success(`${selectedReport} data processed successfully!`);
      playSuccessSound();
      
      setSelectedReport('');
      setSelectedFile(null);
      const fileInput = document.getElementById('file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      console.error('Error processing file:', error);
      toast.error('Error processing file. Please check the file format.');
      playErrorSound();
    } finally {
      setLoading(false);
    }
  };

  const processFile = async (file: File, reportType: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const result = e.target?.result;
          let content = '';
          
          if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
            const workbook = XLSX.read(result, { type: 'binary' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            content = XLSX.utils.sheet_to_csv(worksheet);
            console.log('Excel file converted to CSV:', content.substring(0, 500));
          } else {
            content = result as string;
          }
          
          if (reportType === 'PaySprint Credit Ledger') {
            const processedData = processPaySprintCreditLedger(content);
            resolve(processedData);
          } else {
            const basicData = {
              fileName: file.name,
              fileSize: file.size,
              fileType: file.type,
              lastModified: new Date(file.lastModified).toISOString(),
              content: content.substring(0, 1000)
            };
            resolve(basicData);
          }
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      
      if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
        reader.readAsBinaryString(file);
      } else {
        reader.readAsText(file);
      }
    });
  };

  const processPaySprintCreditLedger = (content: string) => {
    const lines = content.trim().split('\n');
    const records = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      let parts;
      if (line.includes(',')) {
        parts = line.split(',').map(p => p.trim().replace(/"/g, ''));
      } else if (line.includes('\t')) {
        parts = line.split('\t').map(p => p.trim());
      } else {
        parts = line.split(/\s+/);
      }
      
      if (parts.length >= 12) {
        try {
          const record = {
            sno: parts[0],
            username: parts[1],
            openingAmount: parseFloat(parts[2]) || 0,
            amount: parseFloat(parts[3]) || 0,
            commission: parseFloat(parts[4]) || 0,
            tds: parseFloat(parts[5]) || 0,
            gst: parseFloat(parts[6]) || 0,
            closingAmount: parseFloat(parts[7]) || 0,
            type: parts[8],
            narrative: parts[9] || '',
            txnType: parts[10] || '',
            date: parts[11] || '',
            time: parts[12] || ''
          };
          
          if (record.sno !== 'SNO' && !isNaN(parseFloat(record.sno)) && parseFloat(record.sno) >= 1) {
            records.push(record);
          }
        } catch (error) {
          console.warn('Error parsing line:', line, error);
        }
      }
    }
    
    const summary = {
      totalRecords: records.length,
      totalAmount: records.reduce((sum, r) => sum + r.amount, 0),
      totalCommission: records.reduce((sum, r) => sum + r.commission, 0),
      totalTDS: records.reduce((sum, r) => sum + r.tds, 0),
      totalGST: records.reduce((sum, r) => sum + r.gst, 0),
      creditRecords: records.filter(r => r.type.toLowerCase() === 'credit').length,
      debitRecords: records.filter(r => r.type.toLowerCase() === 'debit').length,
      totalDebitFunds: records.filter(r => r.type.toLowerCase() === 'debit').reduce((sum, r) => sum + r.amount, 0),
      trfToDebitWallet: 0, trfToDebitWalletRecords: 0,
      trfToBankAccount: 0, trfToBankAccountRecords: 0,
      twoFACharges: 0, twoFAChargesRecords: 0,
      settlementRefund: 0, settlementRefundRecords: 0,
      totalCreditFunds: records.filter(r => r.type.toLowerCase() === 'credit').reduce((sum, r) => sum + r.amount, 0),
      aepsCW: 0, aepsCWRecords: 0,
      aadhaarPay: 0, aadhaarPayRecords: 0,
      miniStatement: 0, miniStatementRecords: 0,
      totalCommissionCredit: 0, totalCommissionCreditRecords: 0,
      differenceInDebitFunds: 0,
      differenceInCreditFunds: 0,
    };
    
    const walletTransactions = records.filter(r => (r.narrative.toLowerCase().includes('main wallet') || r.narrative.toLowerCase().includes('wallet')) && r.type.toLowerCase() === 'debit');
    summary.trfToDebitWallet = walletTransactions.reduce((sum, r) => sum + r.amount, 0);
    summary.trfToDebitWalletRecords = walletTransactions.length;
    
    const bankTransactions = records.filter(r => (r.narrative.toLowerCase().includes('settlement') || r.narrative.toLowerCase().includes('bank')) && r.type.toLowerCase() === 'debit');
    summary.trfToBankAccount = bankTransactions.reduce((sum, r) => sum + r.amount, 0);
    summary.trfToBankAccountRecords = bankTransactions.length;
    
    const twoFATransactions = records.filter(r => (r.narrative.toLowerCase().includes('merchant two factor') || r.narrative.toLowerCase().includes('2fa')) && r.type.toLowerCase() === 'debit');
    summary.twoFACharges = twoFATransactions.reduce((sum, r) => sum + r.amount, 0);
    summary.twoFAChargesRecords = twoFATransactions.length;
    
    const refundTransactions = records.filter(r => r.narrative.toLowerCase().includes('refund') && (r.txnType.toLowerCase().includes('refund') || r.narrative.toLowerCase().includes('refund of')));
    summary.settlementRefund = refundTransactions.reduce((sum, r) => sum + r.amount, 0);
    summary.settlementRefundRecords = refundTransactions.length;

    const aepsCWTransactions = records.filter(r => (r.narrative.toLowerCase().includes('aeps wallet') || r.narrative.toLowerCase().includes('aeps')) && r.type.toLowerCase() === 'credit');
    summary.aepsCW = aepsCWTransactions.reduce((sum, r) => sum + r.amount, 0);
    summary.aepsCWRecords = aepsCWTransactions.length;

    const aadhaarPayTransactions = records.filter(r => (r.narrative.toLowerCase().includes('aadhaarpay') || r.narrative.toLowerCase().includes('aadhaar')) && r.type.toLowerCase() === 'credit');
    summary.aadhaarPay = aadhaarPayTransactions.reduce((sum, r) => sum + r.amount, 0);
    summary.aadhaarPayRecords = aadhaarPayTransactions.length;

    const miniStatementTransactions = records.filter(r => (r.narrative.toLowerCase().includes('ministatement') || r.narrative.toLowerCase().includes('mini statement')) && r.type.toLowerCase() === 'credit');
    summary.miniStatement = miniStatementTransactions.reduce((sum, r) => sum + r.commission + r.tds, 0);
    summary.miniStatementRecords = miniStatementTransactions.length;

    const totalCommissionCreditTransactions = records.filter(r => r.type.toLowerCase() === 'credit' && r.commission > 0);
    summary.totalCommissionCredit = totalCommissionCreditTransactions.reduce((sum, r) => sum + r.commission, 0);
    summary.totalCommissionCreditRecords = totalCommissionCreditTransactions.length;
    
    summary.differenceInDebitFunds = summary.totalDebitFunds - (summary.trfToDebitWallet + summary.trfToBankAccount + summary.twoFACharges);
    summary.differenceInCreditFunds = summary.totalCreditFunds - (summary.aepsCW + summary.aadhaarPay);
    
    return {
      type: 'PaySprint Credit Ledger',
      records,
      summary,
    };
  };

  const SummaryCard = ({ title, value, records, footerText, color = 'gray' }: { title: string, value: string, records?: number, footerText?: string, color?: string }) => {
    const colors: { [key: string]: string } = {
      red: 'border-red-500 text-red-700',
      green: 'border-green-500 text-green-700',
      blue: 'border-blue-500 text-blue-700',
      purple: 'border-purple-500 text-purple-700',
      orange: 'border-orange-500 text-orange-700',
      indigo: 'border-indigo-500 text-indigo-700',
      gray: 'border-gray-500 text-gray-700',
    };
    return (
      <div className={`bg-white p-3 rounded-lg border-l-4 ${colors[color]}`}>
        <div className="text-xs font-medium text-gray-500">{title}</div>
        <div className="text-xl font-bold">{value}</div>
        {records !== undefined && <div className="text-xs text-gray-400">{records} records</div>}
        {footerText && <div className="text-xs text-gray-400">{footerText}</div>}
      </div>
    );
  };

  const ProcessedReportView = ({ item, user, setLoadingState }: { item: any, user: any, setLoadingState: (loading: boolean) => void }) => {
    const reportContentRef = useRef<HTMLDivElement>(null);
    const [isSharing, setIsSharing] = useState(false);

    const handleShareReport = async () => {
      if (!reportContentRef.current) {
        toast.error('Cannot share report. Content not found.');
        return;
      }

      setIsSharing(true);
      toast('Preparing image, please wait...', { icon: '📸' });

      try {
        const canvas = await html2canvas(reportContentRef.current, {
          useCORS: true,
          scale: 2.5,
          backgroundColor: '#ffffff',
        });
        
        canvas.toBlob(async (blob) => {
          if (!blob) {
            toast.error('Failed to create image from report.');
            setIsSharing(false);
            return;
          }

          const formData = new FormData();
          formData.append('file', blob, 'report-summary.png');
          formData.append('payload_json', JSON.stringify({
            content: `📊 **${item.reportType}** Summary shared by **${user?.name || 'Unknown User'}**`,
          }));

          const response = await postToDiscord(formData);

          if (response.ok) {
            toast.success('Report shared to Discord!');
            playSuccessSound();
          } else {
            const errorText = await response.text();
            toast.error(`Discord API Error: ${response.status}`);
            console.error('Discord API Error:', response.status, errorText);
            playErrorSound();
          }
        }, 'image/png');

      } catch (error) {
        console.error('Error sharing report:', error);
        toast.error('Failed to share report.');
        playErrorSound();
      } finally {
        setTimeout(() => setIsSharing(false), 1000);
      }
    };

    return (
      <div ref={reportContentRef} className="bg-white rounded-lg shadow-md">
        <div className="flex justify-between items-center p-3 border-b bg-gray-50 rounded-t-lg">
          <div>
            <h3 className="text-md font-semibold text-gray-800">{item.reportType}</h3>
            <p className="text-xs text-gray-500">
              Generated on: {new Date(item.timestamp).toLocaleString()}
            </p>
          </div>
          <button
            onClick={handleShareReport}
            disabled={isSharing}
            className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white font-medium py-1 px-3 rounded-md text-sm flex items-center gap-1.5 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" /></svg>
            {isSharing ? 'Sharing...' : 'Share Report'}
          </button>
        </div>
        
        {item.reportType === 'PaySprint Credit Ledger' && item.data.type === 'PaySprint Credit Ledger' ? (
          <div>
            <div className="p-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <SummaryCard title="Total Debit" value={`₹${item.data.summary.totalDebitFunds.toFixed(2)}`} records={item.data.summary.debitRecords} color="red" />
                <SummaryCard title="TRF to Wallet" value={`₹${item.data.summary.trfToDebitWallet.toFixed(2)}`} records={item.data.summary.trfToDebitWalletRecords} color="blue" />
                <SummaryCard title="TRF to Bank" value={`₹${item.data.summary.trfToBankAccount.toFixed(2)}`} records={item.data.summary.trfToBankAccountRecords} color="blue" />
                <SummaryCard title="2FA Charges" value={`₹${item.data.summary.twoFACharges.toFixed(2)}`} records={item.data.summary.twoFAChargesRecords} color="orange" />
                <SummaryCard title="Debit Difference" value={`₹${item.data.summary.differenceInDebitFunds.toFixed(2)}`} footerText="Calculated" color="red" />
                
                <SummaryCard title="Total Credit" value={`₹${item.data.summary.totalCreditFunds.toFixed(2)}`} records={item.data.summary.creditRecords} color="green" />
                <SummaryCard title="AEPS CW" value={`₹${item.data.summary.aepsCW.toFixed(2)}`} records={item.data.summary.aepsCWRecords} color="green" />
                <SummaryCard title="AadhaarPay" value={`₹${item.data.summary.aadhaarPay.toFixed(2)}`} records={item.data.summary.aadhaarPayRecords} color="green" />
                <SummaryCard title="Mini Statement" value={`₹${item.data.summary.miniStatement.toFixed(2)}`} records={item.data.summary.miniStatementRecords} color="indigo" />
                <SummaryCard title="Credit Difference" value={`₹${item.data.summary.differenceInCreditFunds.toFixed(2)}`} footerText="Calculated" color="green" />

                <SummaryCard title="Settlement Refund" value={`₹${item.data.summary.settlementRefund.toFixed(2)}`} records={item.data.summary.settlementRefundRecords} color="purple" />
                <SummaryCard title="Total Commission" value={`₹${item.data.summary.totalCommissionCredit.toFixed(2)}`} records={item.data.summary.totalCommissionCreditRecords} color="purple" />
              </div>
            </div>
            
            <div className="p-4">
              <h4 className="font-semibold mb-2 text-md">Transaction Records ({item.data.summary.totalRecords})</h4>
              <div className="overflow-x-auto max-h-[60vh] border rounded-lg">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-100 sticky top-0 z-10">
                    <tr>
                      <th className="px-2 py-2 text-left font-medium">S.No</th>
                      <th className="px-2 py-2 text-left font-medium">Username</th>
                      <th className="px-2 py-2 text-right font-medium">Opening</th>
                      <th className="px-2 py-2 text-right font-medium">Amount</th>
                      <th className="px-2 py-2 text-right font-medium">Commission</th>
                      <th className="px-2 py-2 text-right font-medium">Closing</th>
                      <th className="px-2 py-2 text-center font-medium">Type</th>
                      <th className="px-2 py-2 text-left font-medium">Narrative</th>
                      <th className="px-2 py-2 text-center font-medium">Txn Type</th>
                      <th className="px-2 py-2 text-center font-medium">Date & Time</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {item.data.records.map((record: any, idx: number) => (
                      <tr key={idx} className={`hover:bg-gray-50 ${record.type.toLowerCase() === 'credit' ? 'bg-green-50/50' : 'bg-red-50/50'}`}>
                        <td className="px-2 py-1 text-center">{record.sno}</td>
                        <td className="px-2 py-1 font-mono">{record.username}</td>
                        <td className="px-2 py-1 text-right font-mono">₹{record.openingAmount.toFixed(2)}</td>
                        <td className={`px-2 py-1 text-right font-bold font-mono ${record.type.toLowerCase() === 'credit' ? 'text-green-700' : 'text-red-700'}`}>
                          ₹{record.amount.toFixed(2)}
                        </td>
                        <td className="px-2 py-1 text-right font-mono text-purple-700">₹{record.commission.toFixed(2)}</td>
                        <td className="px-2 py-1 text-right font-mono">₹{record.closingAmount.toFixed(2)}</td>
                        <td className="px-2 py-1 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            record.type.toLowerCase() === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {record.type}
                          </span>
                        </td>
                        <td className="px-2 py-1 max-w-xs truncate" title={record.narrative}>
                          {record.narrative}
                        </td>
                        <td className="px-2 py-1 text-center font-mono">{record.txnType}</td>
                        <td className="px-2 py-1 text-center font-mono whitespace-nowrap">{record.date} {record.time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4">
            <h4 className="font-medium mb-2">File Information:</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><strong>Size:</strong> {(item.data.fileSize / 1024).toFixed(2)} KB</div>
              <div><strong>Type:</strong> {item.data.fileType || 'Unknown'}</div>
              <div><strong>Last Modified:</strong> {new Date(item.data.lastModified).toLocaleString()}</div>
            </div>
            <div className="mt-3">
              <strong>Preview:</strong>
              <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-auto max-h-48">
                {item.data.content}
              </pre>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex-1 flex items-center">
              <img src="/logo.png" alt="Logo" className="h-10 w-auto" />
            </div>
            <div className="flex-1 text-center">
              <h1 className="text-xl font-bold text-gray-800">API Records Dashboard</h1>
            </div>
            <div className="flex-1 flex justify-end items-center">
              <button
                onClick={() => window.history.back()}
                className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                Back
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {/* Form Section */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h2 className="text-md font-semibold mb-3 border-b pb-2">Upload Report File</h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="md:col-span-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Report Name
                </label>
                <select
                  value={selectedReport}
                  onChange={(e) => setSelectedReport(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                  required
                >
                  <option value="">Select Report Type</option>
                  {reportTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="md:col-span-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Attach Report File
                </label>
                <div className="flex items-center">
                  <input
                    id="file-input"
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    accept=".csv,.xlsx,.xls,.txt,.json"
                  />
                  <label
                    htmlFor="file-input"
                    className="cursor-pointer bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-md border border-gray-300 text-sm font-medium"
                  >
                    Browse...
                  </label>
                  <span className="text-xs text-gray-500 ml-3">
                    {selectedFile ? selectedFile.name : 'No file selected'}
                  </span>
                </div>
              </div>

              <div className="md:col-span-1">
                <button
                  type="submit"
                  disabled={loading || !selectedReport || !selectedFile}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  {loading ? 'Processing...' : 'Process File'}
                </button>
              </div>
            </div>
          </form>
        </div>
        
        {/* Data Display Section */}
        {processedData.length > 0 && (
          <div>
            <h2 className="text-md font-semibold mb-3">Processed Data</h2>
            <div className="space-y-6">
              {processedData.map((item) => (
                <ProcessedReportView key={item.timestamp} item={item} user={user} setLoadingState={setLoading} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default APIRecords; 